import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { UserPlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";

interface CreateInquiryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId: string;
  senderName: string | null;
  senderEmail: string | null;
  onCreated: () => void;
}

export function CreateInquiryModal({ 
  open, 
  onOpenChange, 
  threadId, 
  senderName, 
  senderEmail,
  onCreated 
}: CreateInquiryModalProps) {
  // Parse sender name into first/last name
  const nameParts = (senderName || '').split(' ');
  const defaultFirstName = nameParts[0] || '';
  const defaultLastName = nameParts.slice(1).join(' ') || '';

  const [formData, setFormData] = useState({
    first_name: defaultFirstName,
    last_name: defaultLastName,
    email: senderEmail || '',
    phone: '',
    venue: '',
    message: '',
  });
  const [creating, setCreating] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!formData.first_name.trim() || !formData.email.trim()) {
      toast({
        title: "Required fields missing",
        description: "First name and email are required",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    try {
      // Create the inquiry
      const { data: inquiry, error: inquiryError } = await supabase
        .from("inquiries")
        .insert({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          venue: formData.venue.trim() || null,
          message: formData.message.trim() || null,
          status: 'new',
        })
        .select("id")
        .single();

      if (inquiryError) throw inquiryError;

      // Link the thread to the new inquiry
      const { error: linkError } = await supabase
        .from("email_threads")
        .update({ 
          inquiry_id: inquiry.id,
          sender_name: null,
          sender_email: null
        })
        .eq("id", threadId);

      if (linkError) throw linkError;

      toast({
        title: "Success",
        description: "Inquiry created and linked to thread",
      });
      onCreated();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error creating inquiry:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create inquiry",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Create Inquiry
          </DialogTitle>
          <DialogDescription>
            Create a new inquiry from this email sender and link it to this thread.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="email@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="venue">Venue</Label>
            <Input
              id="venue"
              value={formData.venue}
              onChange={(e) => handleChange('venue', e.target.value)}
              placeholder="Wedding venue"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Notes</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Any notes about this inquiry..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Create Inquiry
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}